/**
 * Vote page
 *
 * Main voting experience with state machine flow
 */

import { useTranslation } from 'react-i18next';
import { AlertCircle, ImageOff, RefreshCw } from 'lucide-react';
import { useVoteFlow } from '../hooks/useVoteFlow';
import { VotingScreen, ReasonSelector } from '../components/voting';
import { RevealScreen } from '../components/reveal';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';

export function Vote() {
  const { t } = useTranslation();

  const {
    state,
    currentImage,
    revealedImage,
    userVote,
    userReasons,
    contradiction,
    imageCount,
    error,
    vote,
    submitReasons,
    skip,
    nextImage,
    retry,
  } = useVoteFlow();

  // Loading state
  if (state === 'loading') {
    return (
      <div
        className="container-app flex min-h-[60vh] items-center justify-center py-12"
        aria-busy="true"
        aria-live="polite"
      >
        <div className="text-center" role="status">
          <div
            className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-accent border-t-transparent"
            aria-hidden="true"
          />
          <p className="mt-4 text-gray-600 dark:text-gray-200">
            {t('voting.loading')}
          </p>
          <span className="sr-only">{t('voting.loading')}</span>
        </div>
      </div>
    );
  }

  // Error state
  if (state === 'error') {
    return (
      <div className="container-app flex min-h-[60vh] items-center justify-center py-12">
        <Card variant="bordered" className="max-w-md text-center" role="alert" aria-live="assertive">
          <CardContent>
            <AlertCircle className="mx-auto h-12 w-12 text-vote-no" aria-hidden="true" />
            <h2 className="mt-4 text-xl font-semibold">{t('common.error')}</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-200">
              {error || 'An unexpected error occurred'}
            </p>
            <Button
              variant="primary"
              onClick={retry}
              className="mt-6"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {t('common.try_again')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No more images state
  if (state === 'no-more-images') {
    return (
      <div className="container-app flex min-h-[60vh] items-center justify-center py-12">
        <Card variant="bordered" className="max-w-md text-center" role="status" aria-live="polite">
          <CardContent>
            <ImageOff className="mx-auto h-12 w-12 text-gray-400" aria-hidden="true" />
            <h2 className="mt-4 text-xl font-semibold">No More Images</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-200">
              You've voted on all available images. Check back later for new additions!
            </p>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-200">
              You voted on {imageCount} images this session.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Presenting state - show image and vote buttons
  if (state === 'presenting' && currentImage) {
    return (
      <div className="container-app py-8 sm:py-12">
        <VotingScreen
          image={currentImage}
          imageCount={imageCount}
          onVote={vote}
          onSkip={skip}
        />
      </div>
    );
  }

  // Selecting reasons state
  if (state === 'selecting-reasons' && userVote !== null) {
    return (
      <div className="container-app py-8 sm:py-12">
        <ReasonSelector
          liked={userVote}
          onSubmit={submitReasons}
        />
      </div>
    );
  }

  // Revealing state
  if (state === 'revealing' && revealedImage && userVote !== null) {
    return (
      <div className="container-app py-8 sm:py-12">
        <RevealScreen
          image={revealedImage}
          userVote={userVote}
          userReasons={userReasons}
          contradiction={contradiction}
          onNext={nextImage}
        />
      </div>
    );
  }

  // Fallback (should not happen)
  return null;
}
